import { useState, useRef, useMemo, useEffect, MouseEventHandler, useCallback } from "react";
import clsx from "clsx";
// Helpers
import { getFormatUTCDate } from "@/Common/helpers/Dates";
import { applyFormatter, getDealStatusClassName } from "@/Washouts/helpers/tables";
import { getCellClassNameFn, isFooterCell } from "@/Common/helpers/Tables";
import { errorLogger, getError } from "@/Common/helpers/logger";
// Components
import AddCellIssues from "@/Layouts/components/Tables/DataSmartTable/additional/AddCellIssues";
import CommonToolbar from "@/Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import NoRowsOverlay from "@/Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import TableLoader from "@/Layouts/components/Skeletons/TableLoader";
import { TypographyCell } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
// Materials
import { Collapse, Divider, Grid } from "@mui/material";
import { GridCellProps, GridColDef, GridValidRowModel, useGridApiRef } from "@mui/x-data-grid-premium";
// Types
import { DealsTableProps, FooterModelsToShow, Product } from "@/Washouts/types/sales";
import { COLUMN_RESIZE_FACTOR, DEFAULT_COLLAPSED_SIZE, HEADER_CLASS_NAME, ID_GRID_COLDEF } from "@/Washouts/constants";
import { StringMap } from "@/Common/interfaces/types";
import { GridStatePremium } from "@mui/x-data-grid-premium/models/gridStatePremium";
import { Issue } from "@/Common/types/tables";
// Icons
import CircleRoundedIcon from "@mui/icons-material/CircleRounded";
import { DATE_FORMAT } from "@/Constants/date";

const REFERENCE = "PendingDeals";
const TITLE = "Pending Deals";
const WIDE_COLUMN_WIDTH = 150;

const buildColumns = (items: Product[] = [], key = "", issues: Issue[] = []) => {
	return items.map((element: Product) => {
		const colDef: GridColDef = {
			field: `${key}_${element.product}`,
			headerName: element.product,
			type: "number",
			width: 110,
			resizable: false,
			align: "right",
			filterable: false,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, { isFunded: false, isMoney: true }, {}),
			renderCell: (params) => (
				<TypographyCell
					render="absolute"
					reference={REFERENCE}
					params={params}
					issues={issues}
					hints={{ isFunded: false, isMoney: true }}
				/>
			),
			valueGetter: (_value: number, row: GridValidRowModel) => {
				const product = row?.[key]?.filter((item: Product) => item.product === element.product);
				return product?.[0]?.value ?? 0;
			},
			renderHeader: (params) => <TypographyHeader params={params} left />,
		};
		return colDef;
	});
};

function getInitialColDef(issues: Issue[]): GridColDef[] {
	return [
		ID_GRID_COLDEF,
		{
			field: "saleDate",
			headerName: "Sale Date",
			type: "date",
			width: 80,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			valueFormatter: (value) => getFormatUTCDate(value, DATE_FORMAT.usDateDashShortYear),
			renderCell: (params) => {
				if (isFooterCell(params)) {
					return <TypographyCell hints={{ isFooter: true, noWrap: true, noFocus: true }}>Total</TypographyCell>;
				}
				return <TypographyCell reference={REFERENCE} params={params} issues={issues} />;
			},
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "dealNumber",
			headerName: "Deal Number",
			width: 80,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			renderCell: (params) => <TypographyCell reference={REFERENCE} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "dealStatus",
			headerName: "Deal Status",
			width: 90,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			renderCell: (row) => {
				if (!row.value) {
					return null;
				}
				return (
					<TypographyCell>
						<CircleRoundedIcon className={`${getDealStatusClassName(row.value)} !text-[12px] -mt-[3px] mr-[5px]`} />
						{row.value}
					</TypographyCell>
				);
			},
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "customerName",
			headerName: "Customer Name",
			width: WIDE_COLUMN_WIDTH,
			minWidth: WIDE_COLUMN_WIDTH,
			maxWidth: WIDE_COLUMN_WIDTH * COLUMN_RESIZE_FACTOR,
			flex: 1,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, { noWrap: true }, {}),
			renderCell: (params) => <TypographyCell reference={REFERENCE} params={params} issues={issues} hints={{ noWrap: true }} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "salesAssociateName",
			headerName: "Sales Associate Name",
			width: WIDE_COLUMN_WIDTH,
			minWidth: WIDE_COLUMN_WIDTH,
			maxWidth: WIDE_COLUMN_WIDTH * COLUMN_RESIZE_FACTOR,
			flex: 1,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, { noWrap: true }, {}),
			renderCell: (params) => <TypographyCell reference={REFERENCE} params={params} issues={issues} hints={{ noWrap: true }} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "dealManagerName",
			headerName: "Deal Manager Name",
			width: WIDE_COLUMN_WIDTH,
			minWidth: WIDE_COLUMN_WIDTH,
			maxWidth: WIDE_COLUMN_WIDTH * COLUMN_RESIZE_FACTOR,
			flex: 1,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, { noWrap: true }, {}),
			renderCell: (params) => <TypographyCell reference={REFERENCE} params={params} issues={issues} hints={{ noWrap: true }} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "stockNumber",
			headerName: "Stock Number",
			width: 75,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			renderCell: (params) => <TypographyCell reference={REFERENCE} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "saleType",
			headerName: "Sale Type",
			minWidth: 55,
			maxWidth: 55,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			renderCell: (params) => <TypographyCell reference={REFERENCE} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "units",
			headerName: "Units",
			type: "number",
			minWidth: 55,
			maxWidth: 55,
			align: "right",
			headerClassName: `${HEADER_CLASS_NAME} header-end`,
			cellClassName: getCellClassNameFn(issues, { isFunded: false }, {}),
			valueFormatter: (params) => applyFormatter(params, "account"),
			renderCell: (params) => (
				<TypographyCell render="account" reference={REFERENCE} params={params} issues={issues} hints={{ isFunded: false }} />
			),
			renderHeader: (params) => <TypographyHeader params={params} right />,
		},
	];
}

function DealsTable(props: Readonly<DealsTableProps>) {
	const emptyTitle = props?.emptyTitle ?? TITLE;
	const apiRef = useGridApiRef();
	const [totalColumns, setTotalColumns] = useState<GridColDef[]>([]);
	const [footerModels, setFooterModels] = useState<FooterModelsToShow>(null);
	const [showColumns, setShowColumns] = useState(false);
	const [columnsToExpand, setColumnsToExpand] = useState<string[]>([]);
	const [collapsed, setCollapsed] = useState(true);

	// Issues support attributes
	const [openAddIssues, setOpenAddIssues] = useState(false);
	const [anchorAddIssues, setAnchorAddIssues] = useState<(HTMLDivElement & EventTarget) | null>(null);
	const [issues, setIssues] = useState<Issue[]>([]);
	const activeIssue = useRef<Issue>(null);

	const handleClickCell: MouseEventHandler<HTMLDivElement> = (event) => {
		activeIssue.current = {
			rowReference: event.currentTarget?.parentElement?.dataset.id, // _id
			columnReference: event.currentTarget.dataset.field, // field
		};
		setAnchorAddIssues(event.currentTarget);
		setOpenAddIssues(true);
	};

	const updateCurrentIssue = (color: string, currentIssues: Issue[]) => {
		let isPresent = false;
		for (const issue of currentIssues) {
			if (
				issue &&
				issue.rowReference === activeIssue?.current?.rowReference &&
				issue.columnReference === activeIssue?.current?.columnReference
			) {
				issue.color = color;
				isPresent = true;
				break;
			}
		}

		if (!isPresent) {
			currentIssues.push({
				tableReference: props?.issuesReference,
				rowReference: activeIssue?.current?.rowReference ?? "",
				columnReference: activeIssue?.current?.columnReference ?? "",
				color,
			});
		}
		return currentIssues;
	};

	const handleChangeIssues = (color: string | null) => {
		let currentIssues = [...issues];
		if (color) {
			currentIssues = updateCurrentIssue(color, currentIssues);
		} else {
			currentIssues?.forEach((issue, key) => {
				if (
					issue?.rowReference === activeIssue?.current?.rowReference &&
					issue?.columnReference === activeIssue?.current?.columnReference
				) {
					currentIssues[key] = null;
				}
			});
			currentIssues = currentIssues.filter((issue) => issue);
		}

		setIssues(currentIssues);
		props?.onIssuesUpdatedAction?.(currentIssues, props?.issuesReference);
	};

	// Visibility of the columns should be controlled by state, the 'hide' prop will be deprecated in the future.
	const [columnVisibilityModel, setColumnVisibilityModel] = useState<StringMap<boolean>>({
		_id: false,
	});

	useMemo(() => {
		let columns = getInitialColDef(issues);

		try {
			let frontMax = 0;
			let backMax = 0;
			let frontKey = -1;
			let backKey = -1;

			props.data?.forEach((element, key) => {
				if (element?.frontProducts?.length > frontMax) {
					frontMax = element.frontProducts.length;
					frontKey = key;
				}

				if (element?.backProducts?.length > backMax) {
					backMax = element.backProducts.length;
					backKey = key;
				}
			});

			// Front Products
			if (frontKey !== -1) {
				const frontProducts = buildColumns(props.data?.[frontKey].frontProducts, "frontProducts", issues ?? []);
				if (frontProducts?.length) {
					columns = columns.concat(frontProducts);
				}
			}

			// Front Gross Profit
			columns = columns.concat([
				{
					field: "frontGrossProfit",
					headerName: "Total Front Gross",
					type: "number",
					width: 110,
					align: "right",
					resizable: false,
					headerClassName: HEADER_CLASS_NAME,
					cellClassName: getCellClassNameFn(issues, { isFunded: false }, {}),
					renderCell: (params) => (
						<TypographyCell
							render="absolute"
							reference={REFERENCE}
							params={params}
							issues={issues}
							hints={{ isFunded: false }}
						/>
					),
					renderHeader: (params) => <TypographyHeader params={params} left />,
				},
			]);

			// Back Products
			if (backKey !== -1) {
				const backProducts = buildColumns(props.data?.[backKey].backProducts, "backProducts", issues ?? []);
				if (backProducts?.length) {
					columns = columns.concat(backProducts);
				}
			}

			// Back Gross Profit
			columns = columns.concat([
				{
					field: "backGrossProfit",
					headerName: "Total Back Gross",
					type: "number",
					width: 110,
					align: "right",
					resizable: false,
					headerClassName: HEADER_CLASS_NAME,
					cellClassName: getCellClassNameFn(issues, { isFunded: false }, {}),
					renderCell: (params) => (
						<TypographyCell
							render="absolute"
							reference={REFERENCE}
							params={params}
							issues={issues}
							hints={{ isFunded: false }}
						/>
					),
					renderHeader: (params) => <TypographyHeader params={params} left />,
				},
			]);
		} catch (error) {
			errorLogger(getError(error));
		}

		setTotalColumns(columns);
	}, [props.data, issues]);

	const clearFilters = useCallback(() => {
		// Clear filter on purpose
		try {
			const filter = apiRef.current?.state?.filter;
			if (filter) {
				const cleanFilter = {
					filterModel: { items: [], linkOperator: "and", quickFilterLogicOperator: "and", quickFilterValues: [] },
					visibleRows: null,
					visibleRowsLookup: {},
				};

				apiRef.current.setState((prevState) => {
					return {
						...prevState,
						filter: { ...filter, ...cleanFilter },
					} as GridStatePremium;
				});
			}
		} catch (error) {
			errorLogger(getError(error));
		}
	}, [apiRef]);

	useEffect(() => {
		const visibilityModel: StringMap<boolean> = {
			_id: false,
		};
		const defaultColumnsToExpand: string[] = [];
		const footerModelsToShow: FooterModelsToShow = {
			units: "sum",
			frontGrossProfit: "sum",
			backGrossProfit: "sum",
			frontCommission: "sum",
			backCommission: "sum",
			grossCommission: "sum",
		};

		try {
			let frontMax = 0;
			let backMax = 0;
			let frontKey = -1;
			let backKey = -1;

			props.data?.forEach((element, key) => {
				if (element?.frontProducts?.length > frontMax) {
					frontMax = element.frontProducts.length;
					frontKey = key;
				}

				if (element?.backProducts?.length > backMax) {
					backMax = element.backProducts.length;
					backKey = key;
				}
			});

			// Front Products
			if (frontKey !== -1) {
				props.data?.[frontKey].frontProducts.forEach((element) => {
					visibilityModel[`frontProducts_${element.product}`] = true;
					footerModelsToShow[`frontProducts_${element.product}`] = "sum";
					defaultColumnsToExpand.push(`frontProducts_${element.product}`);
				});
			}

			// Back Products
			if (backKey !== -1) {
				props.data?.[backKey].backProducts.forEach((element) => {
					visibilityModel[`backProducts_${element.product}`] = true;
					footerModelsToShow[`backProducts_${element.product}`] = "sum";
					defaultColumnsToExpand.push(`backProducts_${element.product}`);
				});
			}
		} catch (error) {
			errorLogger(getError(error));
		}

		clearFilters();

		setIssues(props.issues ?? []);
		setColumnVisibilityModel(visibilityModel);
		setFooterModels(footerModelsToShow);
		setColumnsToExpand(defaultColumnsToExpand);
		if (typeof props.collapsed !== "undefined") {
			setCollapsed(props.collapsed);
		}

		return () => {
			setShowColumns(true);
			setFooterModels(null);
			setColumnsToExpand([]);
			setColumnVisibilityModel({
				_id: false,
			});
			setCollapsed(true);

			// Issues controls
			setIssues([]);
			activeIssue.current = null;
			setOpenAddIssues(false);
			setAnchorAddIssues(null);
		};
	}, [props, apiRef, clearFilters]);

	if (!totalColumns || !footerModels) {
		return null;
	}

	function getCell(): Partial<GridCellProps> | undefined {
		if (props?.issues) {
			return { onClick: handleClickCell };
		}
	}

	return (
		<>
			<Collapse in={!collapsed} collapsedSize={DEFAULT_COLLAPSED_SIZE} timeout={100}>
				<DataSmartTable
					{...props.tableProps}
					sx={{ width: "1400px" }}
					apiRef={apiRef}
					columns={totalColumns}
					rows={props.data ?? []}
					hideFooter
					disableColumnMenu
					disableRowSelectionOnClick
					disableMultipleRowSelection
					checkboxSelection={false}
					columnVisibilityModel={columnVisibilityModel}
					initialState={{
						aggregation: {
							model: footerModels,
						},
						sorting: {
							sortModel: [{ field: "saleDate", sort: "asc" }],
						},
					}}
					slots={{
						toolbar: CommonToolbar,
						noRowsOverlay: NoRowsOverlay,
					}}
					slotProps={{
						toolbar: {
							titleExpandible: {
								collapsed,
								hide: false,
								collapsible: true,
								title: emptyTitle,
								titleClasses: "page-title",
								sxContainer: { padding: "20px 32px" },
								onClick: (value: boolean) => setCollapsed(value),
							},
							filter: {
								hide: false,
							},
							search: {
								data: props.data ?? [],
								hide: false,
								disabled: false,
							},
							expand: {
								setColumnVisibilityModel,
								hide: false,
								expanded: showColumns,
								columnsCollapsible: columnsToExpand,
								onExpandAction: (expanded: boolean) => {
									setShowColumns(expanded);
								},
							},
						},
						noRowsOverlay: {
							title: `No ${emptyTitle} Data`,
						},
						cell: getCell(),
					}}
					getCellClassName={(params) => {
						// Targeting footer
						if (isFooterCell(params)) {
							// Use this to change the cell class name depending on the cell value
							const negatives = Object.keys(footerModels);
							if (negatives.includes(params.field) && typeof params.value === "number" && params.value < 0) {
								return "aggregation-footer-negative";
							}

							// Append label 'Total' to the cell value in footer
							if (params.field === "saleDate") {
								return "aggregation-footer-total";
							}
						}
						return "";
					}}
					customProps={{
						tableClassName: {
							"[&_.MuiDataGrid-main]:mt-0 [&_.MuiDataGrid-main]:mb-[15px] [&_.MuiDataGrid-main]:mx-[30px]": true,
							"[&_.toolbar-actions]:absolute [&_.toolbar-actions]:top-[15px]": true,
						},
					}}
				/>
				{props.addIssues && (
					<AddCellIssues
						open={openAddIssues}
						anchorEl={anchorAddIssues}
						onCloseAction={() => {
							setOpenAddIssues(false);
							setAnchorAddIssues(null);
						}}
						onChangeAction={handleChangeIssues}
					/>
				)}
			</Collapse>
			<Collapse in={props.divider} collapsedSize={0} timeout={100}>
				<Divider
					className={clsx({
						"!mx-[24px] !border-b !border-[#CCC]": true,
						"pt-0": collapsed,
						"pt-[5px] !mt-[10px]": !collapsed,
					})}
				></Divider>
			</Collapse>
		</>
	);
}

export default function PendingDealsTable(props: Readonly<DealsTableProps>) {
	if (props.loading) {
		return (
			<>
				<Grid className="py-[20px] px-[32px]">
					<TableLoader smallTitle toolbar />
				</Grid>
				{props.divider &&
					(props.sxSectionDivider ? (
						<Divider sx={props.sxSectionDivider ?? {}}></Divider>
					) : (
						<Divider className="!mx-[24px] !border-b !border-[#CCC]"></Divider>
					))}
			</>
		);
	}

	return <>{props.dataPresent && <DealsTable {...props} />}</>;
}
